import { useState, useRef, useEffect, Fragment } from 'react';
import { Upload, Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import { huFmt } from '../utils';
// ── Spinner ────────────────────────────────────────────────────────────────
export function Spinner({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{animation:'spin 0.8s linear infinite'}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"
        strokeDasharray="40 60" strokeLinecap="round"/>
    </svg>
  );
}

// ── Button ─────────────────────────────────────────────────────────────────
export function Btn({ children, onClick, variant='primary', size='md',
    icon: Icon, loading, disabled, className='', type='button', style={}, ...rest }) {
  const sizes = { sm:'0.45rem 0.65rem', md:'0.5rem 0.9rem', lg:'0.65rem 1.2rem' };
  const fontSize = { sm:'0.75rem', md:'0.82rem', lg:'0.9rem' };
  const colors = {
    // brand-token: a Raymond-skinben sárga háttér + sötét felirat (a PhenoFin
    // skinben --brand = --primary és a kontraszt fehér, tehát változatlan)
    primary:   { bg:'var(--brand, var(--primary))', color:'var(--brand-contrast, #fff)', border:'transparent' },
    secondary: { bg:'#1e293b',         color:'#cbd5e1',          border:'#334155' },
    success:   { bg:'var(--success-strong)', color:'#fff',       border:'transparent' },
    danger:    { bg:'var(--danger-strong)',  color:'#fff',       border:'transparent' },
    ghost:     { bg:'transparent',     color:'var(--text-muted)',border:'transparent' },
    outline:   { bg:'transparent',     color:'var(--text)',      border:'var(--border)' },
  };
  const c = colors[variant] || colors.primary;
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading}
      className={className}
      style={{
        display:'inline-flex', alignItems:'center', gap:6,
        padding:sizes[size], fontSize:fontSize[size], fontWeight:500,
        background:c.bg, color:c.color,
        border:`1px solid ${c.border}`, borderRadius:'var(--radius)',
        cursor: (disabled||loading) ? 'not-allowed' : 'pointer',
        opacity: (disabled||loading) ? 0.5 : 1,
        transition:'opacity 0.15s, filter 0.15s',
        whiteSpace:'nowrap',
        ...style,
      }}
      onMouseEnter={e=>{ if(!disabled&&!loading) e.currentTarget.style.filter='brightness(1.15)'; }}
      onMouseLeave={e=>{ e.currentTarget.style.filter='none'; }}
      {...rest}>
      {loading ? <Spinner size={size==='sm'?12:14}/> : Icon ? <Icon size={size==='sm'?12:14}/> : null}
      {children}
    </button>
  );
}

// Fájlok kinyerése egy drop/paste DataTransfer-ből. A sima `.files` mellett a
// `.items[].getAsFile()`-t is olvassa: az Outlook (és más app-ok) gyakran NEM
// valódi fájlként, hanem virtuális-fájl formátumban (CFSTR_FILECONTENTS) adják a
// csatolmányt, amit a böngésző csak a getAsFile() hívásra materializál temp-fájllá.
// FONTOS: szinkron, a drop eseményen belül kell hívni (az items utána érvénytelen).
export function filesFromDataTransfer(dt) {
  const out = [];
  const seen = new Set();
  const add = (f) => {
    if (!f) return;
    const key = `${f.name}|${f.size}|${f.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(f);
  };
  try {
    if (dt.items && dt.items.length) {
      for (const it of dt.items) if (it.kind === 'file') add(it.getAsFile());
    }
  } catch { /* items nem elérhető – marad a .files */ }
  if (dt.files && dt.files.length) for (const f of dt.files) add(f);
  return out;
}

// ── DropButton ─────────────────────────────────────────────────────────────
// Gombszerű file-drop célpont: kattintásra tallózó, drag&drop-ra feltöltés.
export function DropButton({ onFiles, label='Feltöltés', hint='húzd ide, vagy kattints',
    busy, accept='application/pdf,image/*', multiple=true, style={} }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={e=>{ e.preventDefault(); setDrag(true); }}
      onDragLeave={()=>setDrag(false)}
      onDrop={e=>{ e.preventDefault(); setDrag(false); if (!busy) onFiles(filesFromDataTransfer(e.dataTransfer)); }}
      onClick={()=>!busy && ref.current?.click()}
      title={hint}
      style={{
        display:'inline-flex', alignItems:'center', gap:8,
        padding:'0.32rem 0.85rem',
        background: drag ? 'rgba(59,130,246,0.12)' : '#1e293b',
        border:`1px dashed ${drag ? 'var(--primary)' : '#475569'}`,
        borderRadius:'var(--radius)',
        cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
        transition:'all 0.15s', whiteSpace:'nowrap', userSelect:'none',
        ...style,
      }}>
      <input ref={ref} type="file" accept={accept} multiple={multiple} style={{display:'none'}}
        onChange={e=>{ onFiles(e.target.files); e.target.value=''; }}/>
      {busy
        ? <Spinner size={14}/>
        : <Upload size={14} style={{color: drag ? 'var(--primary)' : '#cbd5e1'}}/>}
      <span style={{display:'flex',flexDirection:'column',lineHeight:1.2,textAlign:'left'}}>
        <span style={{fontSize:'0.8rem',fontWeight:600,
          color: drag ? 'var(--primary)' : '#cbd5e1'}}>{label}</span>
        <span style={{fontSize:'0.6rem',color:'var(--text-muted)'}}>{hint}</span>
      </span>
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────
export function Card({ children, title, subtitle, actions, className='', noPad, style={} }) {
  return (
    <div className={className} style={{
      background:'var(--bg-card)', border:'1px solid var(--border)',
      borderRadius:'var(--radius-lg)', boxShadow:'var(--shadow)',
      marginBottom:'1rem', ...style }}>
      {(title || actions) && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'0.9rem 1.1rem', borderBottom:'1px solid var(--border)'}}>
          <div>
            {title && <h3 style={{fontSize:'0.88rem',fontWeight:600}}>{title}</h3>}
            {subtitle && <p style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:2}}>{subtitle}</p>}
          </div>
          {actions && <div style={{display:'flex',gap:8}}>{actions}</div>}
        </div>
      )}
      <div style={noPad ? {} : {padding:'1rem 1.1rem'}}>{children}</div>
    </div>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────
export function Badge({ children, variant='default' }) {
  const v = {
    default:  '#1e293b/#94a3b8',
    primary:  '#1e3a8a/#93c5fd',
    success:  '#064e3b/#6ee7b7',
    warning:  '#78350f/#fcd34d',
    danger:   '#7f1d1d/#fca5a5',
    incoming: '#1e3a8a/#93c5fd',
    outgoing: '#064e3b/#6ee7b7',
    pending:  '#78350f/#fcd34d',
    paid:     '#064e3b/#6ee7b7',
    overdue:  '#7f1d1d/#fca5a5',
  }[variant] || '#1e293b/#94a3b8';
  const [bg, color] = v.split('/');
  return (
    <span style={{display:'inline-flex',alignItems:'center',padding:'0.15rem 0.55rem',
      borderRadius:99, fontSize:'0.68rem', fontWeight:600, background:bg, color,
      whiteSpace:'nowrap'}}>
      {children}
    </span>
  );
}

// ── Amount ─────────────────────────────────────────────────────────────────
export function Amount({ value, currency='HUF', color }) {
  if (value == null) return <span style={{color:'var(--text-dim)'}}>–</span>;
  const dec = currency === 'HUF' ? 0 : 2;
  return (
    <span style={{fontFamily:'var(--mono)', color:color||'var(--text)'}}>
      {huFmt(value, dec)}&nbsp;<span style={{fontSize:'0.78em',color:'var(--text-muted)'}}>{currency}</span>
    </span>
  );
}

// ── Truncate ───────────────────────────────────────────────────────────────
/* Egy sorba szorított szöveg „…"-gel, a TELJES érték natív tooltipben.
 *
 * Miért közös komponens: a három-négy CSS-tulajdonság (`overflow` +
 * `textOverflow` + `whiteSpace` + `minWidth:0`) együtt kell, és bármelyik
 * kihagyása CSENDBEN elrontja — vagy nem csonkol, vagy levágja tooltip nélkül.
 *
 * ⚠ `minWidth: 0` KÖTELEZŐ: flex-gyerekként a böngésző alap `min-width: auto`-t
 *   ad, ami MEGAKADÁLYOZZA a zsugorodást, tehát ellipszis helyett kilóg a szöveg.
 * ⚠ A szülőnek KELL egy szélesség-korlát (`maxWidth`), különben nincs mihez
 *   csonkolni — a táblázat-cella magától addig nyúlik, ameddig kell.
 * ⚠ A `title` a natív tooltip: nincs hozzá JS, működik billentyűzettel/olvasóval,
 *   és a teljes érték SOSEM veszik el. Csak akkor tesszük ki, ha van szöveg. */
export function Truncate({ children, title, style={} }) {
  const text = title ?? (typeof children === 'string' ? children : undefined);
  return (
    <span title={text || undefined} style={{
      display:'block', minWidth:0, overflow:'hidden',
      textOverflow:'ellipsis', whiteSpace:'nowrap', ...style,
    }}>{children}</span>
  );
}

// ── Input ──────────────────────────────────────────────────────────────────
export function Input({ label, error, style={}, className='', ...props }) {
  return (
    <label className={className} style={{display:'flex',flexDirection:'column',gap:4}}>
      {label && <span style={{fontSize:'0.68rem',fontWeight:600,color:'var(--text-muted)',
        textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</span>}
      <input {...props} style={{
        background:'var(--bg-input)', border:`1px solid ${error?'var(--danger)':'var(--border)'}`,
        borderRadius:'var(--radius)', padding:'0.45rem 0.65rem',
        color:'var(--text)', fontSize:'0.85rem', outline:'none',
        transition:'border-color 0.15s', width:'100%', ...style,
      }}
      onFocus={e=>e.target.style.borderColor='var(--primary)'}
      onBlur={e=>e.target.style.borderColor=error?'var(--danger)':'var(--border)'}/>
      {error && <span style={{fontSize:'0.7rem',color:'var(--danger)'}}>{error}</span>}
    </label>
  );
}

// ── Select ─────────────────────────────────────────────────────────────────
export function Select({ label, options=[], style={}, className='', ...props }) {
  return (
    <label className={className} style={{display:'flex',flexDirection:'column',gap:4}}>
      {label && <span style={{fontSize:'0.68rem',fontWeight:600,color:'var(--text-muted)',
        textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</span>}
      <select {...props} style={{
        background:'var(--bg-input)', border:'1px solid var(--border)',
        borderRadius:'var(--radius)', padding:'0.45rem 0.65rem',
        color:'var(--text)', fontSize:'0.85rem', outline:'none', width:'100%', ...style,
      }}>
        {options.map(o => (
          <option key={o.value??o} value={o.value??o}>{o.label??o}</option>
        ))}
      </select>
    </label>
  );
}

// ── Alert ──────────────────────────────────────────────────────────────────
export function Alert({ children, type='info', style={} }) {
  const DANGER = ['rgba(239,68,68,0.1)', 'rgba(239,68,68,0.3)', '#fca5a5'];
  const s = {
    info:    ['rgba(59,130,246,0.1)',  'rgba(59,130,246,0.3)',  '#93c5fd'],
    success: ['rgba(16,185,129,0.1)', 'rgba(16,185,129,0.3)', '#6ee7b7'],
    warning: ['rgba(245,158,11,0.1)', 'rgba(245,158,11,0.3)', '#fcd34d'],
    danger:  DANGER,
    error:   DANGER,   // alias – korábban a hibás 'error' típus kék info-ként renderelt
  }[type] || ['rgba(59,130,246,0.1)', 'rgba(59,130,246,0.3)', '#93c5fd'];
  return (
    <div style={{background:s[0], border:`1px solid ${s[1]}`, borderRadius:'var(--radius)',
      padding:'0.7rem 1rem', color:s[2], fontSize:'0.82rem', marginBottom:'0.75rem',
      ...style}}>
      {children}
    </div>
  );
}

// ── AssetRequiredNotice ────────────────────────────────────────────────────
/* M11 (2026-09-05, wiki/proposals/asset-registry.md): egységes „hiányzó asset"
 * figyelmeztetés — egy MŰKÖDŐ, csak be nem kötött felületre mutat (linkkel).
 * ⚠ NEM ugyanaz a regiszter, mint a „még nem létező funkció" info-doboza a
 * Számlázó programok / NAV Online Számla oldalon: ott nincs hova irányítani, ezért
 * ott CTA nélküli `Alert type="info"` megy, nem ez. */
export function AssetRequiredNotice({ assetType, label, linkTo, children }) {
  return (
    <Alert type="warning" style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
      <span style={{flex:1, minWidth:200}}>
        {children || <>Ehhez a funkcióhoz egy bekötött <b>{label || assetType}</b> kapcsolat kell.</>}
      </span>
      {linkTo && (
        <a href={linkTo} style={{color:'inherit', fontWeight:600, whiteSpace:'nowrap',
          textDecoration:'underline'}}>
          {label || 'Kapcsolat'} beállítása →
        </a>
      )}
    </Alert>
  );
}

// ── QueryState ─────────────────────────────────────────────────────────────
//
// ⚠⚠ AMIÉRT LÉTEZIK (UX_REVIEW P0-2): a lekérdezések többsége csak a `data`-t és
// az `isLoading`-ot olvasta, az `isError`-t nem. Hibánál a `data` az alapértelmezett
// üres tömb maradt, és a felület azt mondta: „Nincs adat." — **a hiba üres adatnak
// álcázta magát**. Ez egy pénzügyi appban a legrosszabb hibaosztály: a felhasználó
// nem téves számot lát, hanem egy HIÁNYT, amit valósnak hisz („nincs banki díj
// ebben a hónapban", „nincs partner-tartozás").
//
// Ezért itt a három állapot KÖTELEZŐEN szétválik: töltés / hiba+Újra / üres.
//
// Használat — a react-query eredményét egészben átadva:
//
//   const q = useQuery({ queryKey:['x'], queryFn: xApi.list });
//   <QueryState query={q} empty={!q.data?.length} emptyText="Nincs tétel.">
//     {…a lista…}
//   </QueryState>
//
// Táblázat belsejébe (`<tbody>`) a `colSpan`-nal, hogy a sor-szerkezet ne törjön:
//
//   <QueryState query={q} colSpan={7} empty={!rows.length} emptyText="Nincs sor." >
//
// A `children` CSAK akkor renderel, ha nincs hiba és nem üres — így a hívó oldalon
// nem marad „és ha hiba van?" ág, amit el lehet felejteni.
//
// ⚠⚠ A JSX-gyerek MOHÓN kiértékelődik: `<QueryState …>{data.meta.x}</QueryState>`
// a szülő renderelésekor lefut, MÉG TÖLTÉS ALATT is, amikor a `data` undefined →
// TypeError. Ha a tartalom a query adatára hivatkozik, add **függvényként**:
//
//   <QueryState query={q} …>{() => <Table rows={q.data.items} />}</QueryState>
//
// A függvény-alak csak a tényleges tartalom-ágon hívódik meg. Ahol a tartalom
// amúgy is null-biztos (üres tömb default), a sima JSX-gyerek is jó.
export const PERMISSION_FALLBACK = 'Bér- és járulékadatot csak a cég adminja láthat.';

/**
 * Jogosultsági állapot (F0-8, INV-37) — a `QueryState` testvére: 403-ra ez jelenik meg,
 * nem hiba-doboz és nem üres lista. Semleges (nem piros), mert nem hiba történt.
 */
export function PermissionState({ text = PERMISSION_FALLBACK, actionLabel, onAction }) {
  return (
    <div role="status" style={{display:'flex',alignItems:'center',justifyContent:'space-between',
      gap:12,flexWrap:'wrap',padding:'1rem',border:'1px solid var(--border)',
      borderRadius:'var(--radius)',color:'var(--text-muted)',fontSize:'0.82rem'}}>
      <span><span aria-hidden="true">🔒 </span>{text}</span>
      {actionLabel && onAction &&
        <Btn size="sm" variant="secondary" onClick={onAction}>{actionLabel}</Btn>}
    </div>
  );
}

export function QueryState({
  query, isLoading, isError, refetch,
  empty = false, emptyText = 'Nincs adat.',
  errorText = 'Az adatok betöltése sikertelen.',
  colSpan, minHeight = 0, children,
}) {
  const loading = query ? query.isLoading : isLoading;
  const failed  = query ? query.isError   : isError;
  const retry   = query ? query.refetch   : refetch;

  const wrap = (node) => colSpan
    ? <tr><td colSpan={colSpan} style={{padding:'1rem'}}>{node}</td></tr>
    : <div style={{minHeight}}>{node}</div>;

  if (loading) return wrap(
    <div style={{display:'flex',alignItems:'center',gap:8,justifyContent:'center',
      padding:'1.5rem',color:'var(--text-muted)',fontSize:'0.8rem'}}>
      <Spinner /> Betöltés…
    </div>
  );

  // INV-28 / INV-37 (F0-8): a 403 JOGOSULTSÁGI állapot, nem hiba és soha nem üres lista.
  // A szöveg a szerver `detail.message`-e; fallback a bér-láthatóság `PAY_FORBIDDEN`-je.
  const err = query ? query.error : null;
  if (failed && err?.response?.status === 403) {
    const d = err.response.data?.detail;
    const msg = (d && typeof d === 'object' && d.message) || (typeof d === 'string' && d)
      || PERMISSION_FALLBACK;
    return wrap(<PermissionState text={msg} />);
  }

  if (failed) return wrap(
    <Alert type="danger" style={{marginBottom:0,display:'flex',alignItems:'center',
      justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
      <span>{errorText}</span>
      {retry && <Btn size="sm" variant="secondary" onClick={()=>retry()}>Újra</Btn>}
    </Alert>
  );

  if (empty) return wrap(
    <div style={{textAlign:'center',color:'var(--text-muted)',
      fontSize:'0.82rem',padding:'2rem 1rem'}}>
      {emptyText}
    </div>
  );

  return typeof children === 'function' ? children() : (children ?? null);
}

// ── Dialógus-viselkedés (Modal + SlideOver közös a11y rétege) ──────────────
// Esc zár, Tab a dialóguson belül marad (fókuszcsapda), a háttér nem
// görgethető, záráskor a fókusz visszatér a megnyitó elemre.
// Egymásra nyíló dialógusoknál (pl. ConfirmDialog egy modálon belül) az Esc és
// a Tab-csapda CSAK a legfelsőre hat – ezt a modul-szintű stack biztosítja.
const _modalStack = [];
function useDialogA11y(open, onClose, boxRef) {
  // ⚠⚠ AZ `onClose` SZÁNDÉKOSAN REF-BEN VAN, NEM AZ EFFECT DEPENDENCY-JÉBEN.
  //
  // 2026-08-05-i hiba: a hívók az `onClose`-t jellemzően INLINE adják át
  // (`onClose={() => finish(false)}`), ami MINDEN renderre új függvény-identitás.
  // Amíg az `onClose` dependency volt, egy modalban lévő inputba írás így ment:
  // leütés → a hívó state-je változik → új `onClose` → a dependency változik →
  // lefut a CLEANUP (`opener?.focus?.()`) ÉS újra az effect (`boxRef.current?.focus()`)
  // → **a fókusz minden karakter után elszállt az inputról a dialógus-konténerre**.
  // Konkrétan a step-up jelszó-ablakban nem lehetett folyamatosan írni.
  //
  // A `boxRef` (useRef) stabil, az `open` csak nyitás/zárásnál változik — így az
  // effect pontosan annyiszor fut, ahányszor kell. NE tedd vissza az `onClose`-t
  // a dependency-listába „a lint miatt"; a ref mindig a legfrissebbet hívja.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const token = {};
    _modalStack.push(token);
    const opener = document.activeElement;
    const onKey = (e) => {
      if (_modalStack[_modalStack.length - 1] !== token) return;   // nem a legfelső
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current?.(); return; }
      if (e.key !== 'Tab' || !boxRef.current) return;
      const focusables = Array.from(boxRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )).filter(el => !el.disabled && el.offsetParent !== null);
      if (!focusables.length) { e.preventDefault(); return; }
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Csak akkor fókuszáljuk a dialógus-konténert, ha a fókusz még NEM a dialóguson
    // belül van. Enélkül az `autoFocus`-os mezőtől (React a mountnál fókuszálja, ez az
    // effect utána fut) elvennénk a fókuszt — a step-up jelszó-mező pl. sosem kapta meg.
    if (!boxRef.current?.contains(document.activeElement)) boxRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      const i = _modalStack.indexOf(token);
      if (i !== -1) _modalStack.splice(i, 1);
      opener?.focus?.();
    };
  }, [open, boxRef]);
}

// ── Modal ──────────────────────────────────────────────────────────────────
// `dismissible=false`: a háttérre kattintás és az Esc NEM zár (csak a ✕ / a gombok).
// 2026-09-24, élő hiba: az utalási ablak egy passkey-ablak bezárása utáni Esc/kattintásra
// az eredménysorokkal együtt eltűnt — ahol eredmény vagy folyamatban lévő művelet van, ott
// a véletlen bezárás adatvesztés.
export function Modal({ open, onClose, title, children, width=480, accentColor, dismissible=true }) {
  const boxRef = useRef(null);
  useDialogA11y(open, dismissible ? onClose : undefined, boxRef);
  if (!open) return null;
  const frameBorder = accentColor ? `2px solid ${accentColor}` : '1px solid var(--border)';
  const headBorder  = accentColor || 'var(--border)';
  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',
      justifyContent:'center',background:'rgba(0,0,0,0.7)',padding:'1rem'}}
      onClick={e=>dismissible&&e.target===e.currentTarget&&onClose?.()}>
      <div ref={boxRef} tabIndex={-1} role="dialog" aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{background:'var(--bg-card)',border:frameBorder,
        borderRadius:'var(--radius-lg)',width:'100%',maxWidth:width,
        maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'var(--shadow)',
        outline:'none'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'0.9rem 1.1rem',borderBottom:`1px solid ${headBorder}`}}>
          <h3 style={{fontWeight:600,fontSize:'0.92rem'}}>{title}</h3>
          <button onClick={onClose} aria-label="Bezárás" style={{background:'none',border:'none',
            color:'var(--text-muted)',cursor:'pointer',fontSize:'1.1rem',lineHeight:1}}>✕</button>
        </div>
        <div style={{padding:'1.1rem',overflowY:'auto',flex:1}}>{children}</div>
      </div>
    </div>
  );
}

// ── SlideOver ──────────────────────────────────────────────────────────────
// Jobbról beúszó részlet-panel (M365 admin minta): a lista fölé nyílik,
// a háttér elhalványul, Esc / háttér-kattintás / ✕ zár. A fejlécet, a görgethető
// törzset és a láblécet a hívó rendereli (flex-oszlop elrendezésben).
export function SlideOver({ open, onClose, width=480, ariaLabel, onKeyDown, children }) {
  const boxRef = useRef(null);
  useDialogA11y(open, onClose, boxRef);
  if (!open) return null;
  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(4,7,13,0.5)'}}
      onClick={e=>e.target===e.currentTarget&&onClose?.()}>
      <style>{`@keyframes slideoverIn{from{transform:translateX(48px);opacity:0.5}to{transform:none;opacity:1}}
        @media (prefers-reduced-motion: reduce){ .slideover-panel{animation:none!important} }`}</style>
      <div ref={boxRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={ariaLabel}
        className="slideover-panel" onKeyDown={onKeyDown}
        style={{position:'absolute',top:0,right:0,bottom:0,width:'100%',maxWidth:width,
          background:'var(--bg-card)',borderLeft:'1px solid var(--border)',
          boxShadow:'-18px 0 48px rgba(0,0,0,0.5)',display:'flex',flexDirection:'column',
          outline:'none',animation:'slideoverIn 0.18s ease-out'}}>
        {children}
      </div>
    </div>
  );
}

// ── ConfirmDialog ──────────────────────────────────────────────────────────
// A window.confirm() styled kiváltása. danger=true → piros keret + piros gomb.
export function ConfirmDialog({ open, onConfirm, onCancel, title='Megerősítés',
    message, confirmLabel='Igen', cancelLabel='Mégse', danger=false, loading=false }) {
  return (
    <Modal open={open} onClose={onCancel} title={title} width={420}
      accentColor={danger ? 'var(--danger)' : undefined}>
      <div style={{fontSize:'0.85rem',lineHeight:1.55,marginBottom:'1rem'}}>{message}</div>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <Btn variant="secondary" onClick={onCancel} disabled={loading}>{cancelLabel}</Btn>
        <Btn variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Btn>
      </div>
    </Modal>
  );
}

// ── StatCard ───────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',
      borderRadius:'var(--radius-lg)',padding:'1.1rem',position:'relative',overflow:'hidden'}}>
      {Icon && <Icon size={34} style={{position:'absolute',top:10,right:10,
        color:color||'var(--text-dim)',opacity:0.15}}/>}
      <p style={{fontSize:'0.65rem',fontWeight:600,color:'var(--text-muted)',
        textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>{label}</p>
      <div style={{fontSize:'1.5rem',fontWeight:700,color:color||'var(--text)',
        lineHeight:1,fontFamily:'var(--mono)'}}>{value}</div>
      {sub && <p style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:4}}>{sub}</p>}
    </div>
  );
}

// ── Pill ───────────────────────────────────────────────────────────────────
// Egységes szűrő-gomb. `size='sm'` a kompakt gyorsszűrő-pilleknek (radius 20,
// mint egy chip), `size='lg'` a hangsúlyos alapszűrőknek (radius var(--radius),
// mint a Bizonylatok Bejövő/Kimenő/B.A.J. váltója). `color` NÉLKÜL a skin
// elsődleges színét használja (`--brand`/`--primary-subtle` — Raymond-skinben
// is helyesen átszíneződik); csak akkor adj `color`-t, ha a pill szemantikusan
// egy konkrét (deviza/irány) színhez van kötve, mert az felülírja a skint.
//
// ⚠ 2026-08-24-i egységesítés előtt 4 helyen (Documents.js, Statements.js,
// misc.js, PeriodFilter.js) volt egy-egy kézzel másolt pill-stílus — a
// misc.js-é hardkódolt `#93c0ff` színt használt a `--primary-subtle` token
// helyett, ezért a Raymond white-label skinben NEM színeződött volna át.
export function Pill({ active, onClick, color, size='sm', children, title, style={} }) {
  const sizes = {
    sm: { padding:'0.28rem 0.7rem', fontSize:'0.72rem', fontWeight:600, radius:20, border:1 },
    lg: { padding:'0.5rem 1.2rem', fontSize:'0.85rem', fontWeight:700, radius:'var(--radius)', border:2 },
  };
  const s = sizes[size] || sizes.sm;
  const activeColor = color || 'var(--brand, var(--primary))';
  const activeBg = color ? `${color}22` : 'var(--primary-subtle)';
  return (
    <button type="button" onClick={onClick} title={title} style={{
      padding:s.padding, fontSize:s.fontSize, fontWeight:s.fontWeight, borderRadius:s.radius,
      cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.15s',
      border:`${s.border}px solid ${active ? activeColor : 'var(--border)'}`,
      background: active ? activeBg : 'transparent',
      color: active ? activeColor : 'var(--text-muted)',
      ...style,
    }}>
      {children}
    </button>
  );
}

// ── SearchField ────────────────────────────────────────────────────────────
// Egységes keresőmező, nagyítólencse-ikonnal és opcionális törlés-gombbal.
// `onChange` minden leütésre fut (élő szűrés/autocomplete-hez), `onEnter` a
// keresés véglegesítésekor (Enter). `children` a mező alá/mellé renderelt
// extra tartalomhoz (pl. autocomplete-javaslatok) — a wrapper `position:relative`,
// tehát egy `position:absolute` gyerek helyesen a mezőhöz igazodik.
export function SearchField({ value, onChange, onEnter, onEscape, onClear,
    placeholder, onFocus, onBlur, style={}, inputStyle={}, children }) {
  return (
    <div style={{position:'relative', ...style}}>
      <Search size={13} style={{position:'absolute', left:9, top:'50%',
        transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none'}}/>
      <input type="text" value={value} placeholder={placeholder}
        onChange={e => onChange?.(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') onEnter?.();
          if (e.key === 'Escape') onEscape?.();
        }}
        onFocus={onFocus} onBlur={onBlur}
        style={{
          width:'100%', background:'var(--bg-input)', border:'1px solid var(--border)',
          borderRadius:'var(--radius)',
          padding:`0.32rem ${value && onClear ? '1.8rem' : '0.65rem'} 0.32rem 1.9rem`,
          color:'var(--text)', fontSize:'0.82rem', outline:'none',
          ...inputStyle,
        }}/>
      {value && onClear ? (
        <button type="button" onClick={onClear} tabIndex={-1} style={{
          position:'absolute', right:6, top:'50%', transform:'translateY(-50%)',
          background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)',
          padding:2, display:'flex', alignItems:'center'}}>
          <X size={12}/>
        </button>
      ) : null}
      {children}
    </div>
  );
}

// ── Checkbox ───────────────────────────────────────────────────────────────
export function Checkbox({ label, checked, onChange, disabled = false, title }) {
  return (
    <label title={title} style={{display:'flex',alignItems:'center',gap:8,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize:'0.85rem',color: disabled ? 'var(--text-muted)' : 'var(--text)'}}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled}
        style={{width:15,height:15,accentColor:'var(--primary)'}}/>
      {label}
    </label>
  );
}

// ── Táblázat-primitív (Table / Th / Td / Tr / ListCard) ────────────────────
// (2026-09-16, Partnerek modul F0 — forrás: `raw/sessions/2026-09-10-partnerek-modul-tervezes.md`,
//  `…-partnerek-agent-anyagok/ux-partner-design.md` 7.2/T1–T4)
//
// MIÉRT: a repóban MÉRVE 25 helyen (8 fájlban) él egy-egy kézzel másolt `thS`/`tdS`
// stílus-pár. Mindegyik külön dönt a betűméretről, a paddingről, és arról, hogy
// van-e egyáltalán `loading` / `empty` / `error` ága — ezért a Bizonylatok táblája
// más sormagasságot ad, mint a Statisztikáké, és üres állapotban a legtöbb helyen
// egyszerűen nem látszik semmi. A Partnerek oldal lett volna a 26. másolat.
//
// A primitív a `Documents.js` (a legérettebb tábla) idiómáját emeli ki:
// ugyanaz a padding, ugyanaz a betűméret, ugyanaz a `SortIcon`, ugyanaz a
// kijelölés-jelölő `inset` sáv — plusz három dolog, ami eddig sehol nem volt meg:
//
//  1. **Sor-a11y** (UX-review P1-7): kattintható sor = `role="link"` + `tabIndex`
//     + Enter/Space. A mai `DocumentRow` csupasz `<tr onClick>`, tehát
//     billentyűzettel elérhetetlen.
//  2. **Egy hívási hely a loading/empty/error ágnak** — a `QueryState` colSpan-os
//     alakja, amit eddig minden tábla külön másolt (vagy kihagyott).
//  3. **Mobil kártya-fallback** (doktrína 1.6/3.: vízszintes görgetés tiltva).
//     `renderCard` megadásával 768px alatt a tábla helyett kártyalista jelenik meg,
//     CSS-media-query-vel — NEM JS resize-figyeléssel, tehát nincs villanás
//     (ugyanaz a minta, mint a `Layout.js` `.mobile-nav`-ja).
//
// ⚠ A MEGLÉVŐ TÁBLÁK ÁTÍRÁSA SZÁNDÉKOSAN NEM RÉSZE ENNEK A KÖRNEK: az eszközön
// nem igazolt átírás regressziós kockázat a napi használatban lévő Bizonylatok/
// Kivonatok oldalon. ÚJ tábla viszont már csak ezzel épülhet.

const TABLE_CSS = `
.ui-table-wrap { display:block; }
.ui-card-list { display:none; flex-direction:column; gap:8px; }
.ui-table tbody tr:focus-visible { outline:2px solid var(--primary); outline-offset:-2px; }
.ui-card:focus-visible { outline:2px solid var(--primary); outline-offset:2px; }
@media (max-width: 768px) {
  .ui-table-wrap { display:none; }
  .ui-card-list { display:flex; }
}`;

const thBaseS = {
  padding:'0.6rem 0.75rem', color:'var(--text-muted)', fontWeight:500,
  fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em',
  whiteSpace:'nowrap', userSelect:'none', textAlign:'left',
  borderBottom:'1px solid var(--border)',
};
const tdBaseS = {
  padding:'0.55rem 0.75rem', color:'var(--text)', verticalAlign:'middle',
  fontSize:'0.8rem', borderBottom:'1px solid var(--border-dim)',
};

function SortIcon({ dir }) {
  if (!dir) return <ChevronUp size={12} style={{opacity:0.2,flexShrink:0}}/>;
  return dir === 'desc'
    ? <ChevronDown size={12} style={{color:'var(--primary)',flexShrink:0}}/>
    : <ChevronUp size={12} style={{color:'var(--primary)',flexShrink:0}}/>;
}

/** Oszlopfej. `sortable` esetén a kattintható rész BELSŐ `<button>` (nem a `<th>`),
 *  mert csak így kap billentyűzet-fókuszt és `aria-sort` bejelentést. */
export function Th({ children, align='left', width, sortable, sortDir, onSort, title, style={} }) {
  const label = sortable ? (
    <button type="button" onClick={onSort} title={title}
      style={{display:'inline-flex',alignItems:'center',gap:4,background:'none',border:'none',
        padding:0,margin:0,font:'inherit',color:'inherit',textTransform:'inherit',
        letterSpacing:'inherit',cursor:'pointer',
        flexDirection: align==='right' ? 'row-reverse' : 'row'}}>
      {children}
      <SortIcon dir={sortDir}/>
    </button>
  ) : children;
  return (
    <th scope="col" title={sortable ? undefined : title}
      aria-sort={sortable
        ? (sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none')
        : undefined}
      style={{...thBaseS, textAlign:align, width, ...style}}>
      {label}
    </th>
  );
}

/** Adatcella. `mono` = `--mono` betűcsalád (szám/azonosító), `accent` = bal éli
 *  `inset` sáv (a `Documents.js` kijelölés-jelölője, ott a 132. sor). */
export function Td({ children, align='left', mono, muted, nowrap, accent,
    colSpan, title, onClick, style={} }) {
  return (
    <td colSpan={colSpan} title={title} onClick={onClick}
      style={{...tdBaseS, textAlign:align,
        fontFamily: mono ? 'var(--mono)' : undefined,
        color: muted ? 'var(--text-muted)' : undefined,
        whiteSpace: nowrap ? 'nowrap' : undefined,
        boxShadow: accent ? `inset 2px 0 0 ${accent === true ? 'var(--primary)' : accent}` : undefined,
        ...style}}>
      {children}
    </td>
  );
}

/** Táblasor. `onClick` esetén link-szemantikát kap (`role="link"`, Enter/Space) —
 *  EGY tabstop/sor, ahogy az UX-review P1-7 kéri. */
export function Tr({ children, onClick, selected, ariaLabel, style={} }) {
  const bg = selected ? 'rgba(59,130,246,0.08)' : 'transparent';
  const on = !!onClick;
  return (
    <tr
      role={on ? 'link' : undefined}
      tabIndex={on ? 0 : undefined}
      aria-label={on ? ariaLabel : undefined}
      onClick={onClick}
      onKeyDown={on ? (e) => {
        // ⚠ A Space alapból GÖRGET — sor-aktiválásnál ez a lap elugrását jelentené.
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
      } : undefined}
      onMouseEnter={on ? (e)=>{ e.currentTarget.style.background='var(--bg-hover)'; } : undefined}
      onMouseLeave={on ? (e)=>{ e.currentTarget.style.background=bg; } : undefined}
      style={{background:bg, cursor: on ? 'pointer' : undefined,
        transition:'background 0.1s', ...style}}>
      {children}
    </tr>
  );
}

/**
 * Adat-vezérelt táblázat egységes fejjel, sor-a11y-vel, loading/empty/error
 * ággal és opcionális mobil kártya-nézettel.
 *
 *   <Table
 *     columns={[{key:'name',label:'Partner',sortable:true},
 *               {key:'count',label:'Bizonylat',align:'right'}]}
 *     rows={partners} rowKey={p=>p.id}
 *     sort={sort} onSort={handleSort}
 *     query={q} emptyText="Nincs partner."
 *     onRowClick={p=>nav(`/partners/${p.id}`)}
 *     rowAriaLabel={p=>`${p.name} részletei`}
 *     renderRow={p => <><Td>{p.name}</Td><Td align="right" mono>{p.count}</Td></>}
 *     renderCard={p => <ListCard title={p.name} fields={[…]}/>}/>
 *
 * ⚠ `renderRow` a CELLÁKAT adja vissza (`<Td>`-ket), nem a `<tr>`-t — a sort a
 * `Table` rendereli, hogy az a11y és a kijelölés-jelölés EGY helyen dőljön el.
 */
export function Table({
  columns = [], rows = [], rowKey, renderRow, renderCard,
  onRowClick, rowAriaLabel, isRowSelected,
  sort, onSort,
  query, isLoading, isError, refetch,
  emptyText = 'Nincs adat.', errorText = 'Az adatok betöltése sikertelen.',
  ariaLabel, caption, footer, style = {},
}) {
  const keyOf = (r, i) => (rowKey ? rowKey(r) : (r?.id ?? i));
  const stateProps = { query, isLoading, isError, refetch,
    empty: !rows.length, emptyText, errorText };

  return (
    <>
      <style>{TABLE_CSS}</style>
      <div className={renderCard ? 'ui-table-wrap' : undefined}
        style={{overflowX: renderCard ? undefined : 'auto', ...style}}>
        <table className="ui-table" aria-label={ariaLabel}
          style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
          {caption && <caption style={{textAlign:'left',captionSide:'top',padding:'0 0 0.5rem',
            color:'var(--text-muted)',fontSize:'0.74rem'}}>{caption}</caption>}
          <thead>
            <tr>
              {columns.map(c => (
                <Th key={c.key} align={c.align} width={c.width} title={c.title}
                  sortable={!!(c.sortable && onSort)}
                  sortDir={sort?.key === c.key ? sort.dir : null}
                  onSort={() => onSort?.(c.key)}>
                  {c.label}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            <QueryState {...stateProps} colSpan={columns.length || 1}>
              {() => rows.map((r, i) => (
                <Tr key={keyOf(r, i)}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  selected={isRowSelected?.(r)}
                  ariaLabel={rowAriaLabel?.(r)}>
                  {renderRow(r, i)}
                </Tr>
              ))}
            </QueryState>
          </tbody>
          {footer}
        </table>
      </div>
      {renderCard && (
        <div className="ui-card-list">
          <QueryState {...stateProps}>
            {() => rows.map((r, i) => (
              <div key={keyOf(r, i)}>{renderCard(r, i)}</div>
            ))}
          </QueryState>
        </div>
      )}
    </>
  );
}

/**
 * Mobil kártya egy táblasor helyett (a `Table` `renderCard`-jának alap-építőköve).
 * SZÁNDÉKOSAN általános — a Bizonylatok oldal is ezt kapja majd, ha a
 * vízszintes görgetést ott is leváltjuk.
 *
 * `fields`: `[{label, value, mono, muted}]` — kétoszlopos címke/érték rács.
 */
export function ListCard({ title, titleRight, subtitle, badges, fields = [],
    onClick, selected, accent, children }) {
  const on = !!onClick;
  return (
    <div className="ui-card"
      role={on ? 'link' : undefined}
      tabIndex={on ? 0 : undefined}
      onClick={onClick}
      onKeyDown={on ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
      } : undefined}
      style={{
        background: selected ? 'rgba(59,130,246,0.08)' : 'var(--bg-card)',
        border:'1px solid var(--border)',
        borderLeft: accent ? `3px solid ${accent}` : '1px solid var(--border)',
        borderRadius:'var(--radius)', padding:'0.7rem 0.8rem',
        cursor: on ? 'pointer' : undefined,
      }}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
        <div style={{minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
            <span style={{fontWeight:600,fontSize:'0.85rem',overflow:'hidden',
              textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{title}</span>
            {badges}
          </div>
          {subtitle && (
            <div style={{fontSize:'0.68rem',color:'var(--text-muted)',
              fontFamily:'var(--mono)',marginTop:2}}>{subtitle}</div>
          )}
        </div>
        {titleRight && <div style={{flexShrink:0,textAlign:'right'}}>{titleRight}</div>}
      </div>
      {fields.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'3px 10px',
          marginTop:8,fontSize:'0.75rem',alignItems:'baseline'}}>
          {fields.map((f, i) => (
            <Fragment key={f.label ?? i}>
              <span style={{color:'var(--text-muted)',fontSize:'0.68rem',
                textTransform:'uppercase',letterSpacing:'0.05em'}}>{f.label}</span>
              <span style={{
                fontFamily: f.mono ? 'var(--mono)' : undefined,
                color: f.muted ? 'var(--text-muted)' : 'var(--text)',
                overflow:'hidden',textOverflow:'ellipsis'}}>{f.value}</span>
            </Fragment>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}
